import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNotificationBar } from 'context/notificationContext';
import InputField from 'components/styledFormElements/InputField';
import IconDropdown from 'components/styledFormElements/IconDropdown';
import { getImageSrc } from 'services/util';
import { useGetIcons } from 'hooks/useIconMaster';
import {
  WATCH_HISTORY_MAX_ITEMS,
  watchHistoryFormSchema,
  WatchHistoryFormValues,
} from 'validations/widgetFields.validation';
import { X } from 'lucide-react';

interface Props {
  widget: {
    value: string | number;
    label: string;
    code: string;
  };
  initialData?: any;
  onSave: (payload: any | null) => void;
  onCancel?: () => void;
}

const WatchHistoryWidget = ({ widget, initialData, onSave, onCancel }: Props) => {
  const notification = useNotificationBar();
  const widgetData = initialData?.data || initialData;

  const [widgetIconName, setWidgetIconName] = useState('');

  const { data: iconsResponse } = useGetIcons();
  const icons = useMemo(
    () =>
      Array.isArray(iconsResponse?.data?.results)
        ? iconsResponse.data.results
        : [],
    [iconsResponse]
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<WatchHistoryFormValues>({
    resolver: yupResolver(watchHistoryFormSchema),
    defaultValues: {
      widgetTitle: widgetData?.widget_title || '',
      maxItems: widgetData?.max_items ?? '',
      widgetIcon: widgetData?.widget_icon || '',
    },
    mode: 'onTouched',
  });

  const widgetIcon = watch('widgetIcon');

  useEffect(() => {
    if (!widgetIcon || !icons.length) {
      setWidgetIconName('');
      return;
    }

    const cleanFileName = widgetIcon.split('/').pop() || '';
    const matchedIcon = icons.find((icon: any) => {
      const iconValue = icon.path || icon.image || icon.icon || '';
      return iconValue.includes(cleanFileName);
    });

    setWidgetIconName(matchedIcon?.name || '');
  }, [widgetIcon, icons]);

  const getRelativeIconPath = (iconPath: string) => {
    if (!iconPath) return '';
    try {
      if (iconPath.startsWith('http')) {
        return new URL(iconPath).pathname.replace(/^\/+/, '');
      }
      return iconPath;
    } catch {
      return iconPath;
    }
  };

  const onSubmit = (formData: WatchHistoryFormValues) => {
    const icon =
      typeof formData.widgetIcon === 'string'
        ? getRelativeIconPath(formData.widgetIcon)
        : '';

    const data = {
      max_items: formData.maxItems,
      widget_title: formData.widgetTitle.trim(),
      widget_code: widget.code,
      widget_type: Number(widget.value),
      view_all: true,
      source_id: '',
      source_type: 'DYNAMIC',
      widget_icon: icon,
      content_rule: 'AUTOMATIC',
      content_type: 'CONTEXTUAL',
    };

    onSave({
      name: widget.label,
      widget_code: widget.code,
      widget_type: Number(widget.value),
      data,
    });

    notification.push({
      title: 'Success',
      message: 'Watch History widget saved successfully',
      type: 'success',
    });
  };

  const handleCancel = () => {
    if (initialData) {
      onSave(initialData);
    } else {
      onSave(null);
    }
    onCancel?.();
  };

  return (
  <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
      <InputField
        label="Widget Title"
        type="text"
        required
        placeholder="Enter widget title"
        register={register('widgetTitle')}
        error={errors.widgetTitle?.message}
      />

      <InputField
        label="Max Items in List"
        type="number"
        required
        min={1}
        max={WATCH_HISTORY_MAX_ITEMS}
        placeholder={`Enter max items (1-${WATCH_HISTORY_MAX_ITEMS})`}
        helpText={`Maximum number of shows to display in the Watch History widget. Range: 1-${WATCH_HISTORY_MAX_ITEMS} shows.`}
        register={register('maxItems', { valueAsNumber: true })}
        error={errors.maxItems?.message}
      />

      <div className="flex flex-col">
        <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
          Widget Icon <span className="text-red-700">*</span>
        </label>
        <Controller
          name="widgetIcon"
          control={control}
          render={({ field }) => (
            <div>
              {field.value ? (
                <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        field.value.startsWith('http')
                          ? field.value
                          : getImageSrc(field.value)
                      }
                      alt="Selected icon"
                      className="w-6 h-6 object-contain dark:bg-white"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">
                      {widgetIconName || 'Icon selected'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      field.onChange('');
                      setWidgetIconName('');
                      clearErrors('widgetIcon');
                    }}
                    className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <IconDropdown
                  onChange={(selectedIcon) => {
                    const iconKey =
                      selectedIcon?.icon ||
                      (selectedIcon as any)?.path ||
                      (selectedIcon as any)?.image ||
                      '';

                    field.onChange(iconKey);
                    setWidgetIconName(selectedIcon?.name || '');
                    if (iconKey) {
                      clearErrors('widgetIcon');
                    }
                  }}
                />
              )}
            </div>
          )}
        />
        {errors.widgetIcon && (
          <p className="text-xs text-red-500 mt-1">{errors.widgetIcon.message}</p>
        )}
      </div>
    </div>

    <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
      <button
        type="button"
        onClick={handleCancel}
        className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        Cancel
      </button>
      <button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Save
      </button>
    </div>
  </form>
  );
};

export default WatchHistoryWidget;
